<script setup>
    import { computed } from 'vue'
    import { useFachbereichStore, useStudiengangStore } from '@/js/stores'
    import { useModuleStore } from '@/js/stores/modul'

    const props = defineProps({
        modulHandbuch: {
            type: Object,
            required: true
        }
    })

    const fachbereichStore = useFachbereichStore()
    const moduleStore = useModuleStore()
    const studiengangStore = useStudiengangStore()

    const TRANSLATIONS_EN = {
        Hochschulzertifikat: 'Certificate (Anhalt University)',
        'ohne Abschluss': 'without degree',
        Vollzeitstudium: 'Full-time program',
        Fernstudium: 'Distance learning program',
        'Berufsbegleitendes Studium': 'Career-integrated program',
        'Duales Studium': 'Dual degree program',
        Orientierungsstudium: 'Science, Technology, Engineering and Math-Orientation Program (STEM)',
        'Duale Studienvariante': 'Dual study option'
    }
    const translate = (value) => TRANSLATIONS_EN[value] ?? value

    const isMhbEnglish = computed(() => {
        const modules = moduleStore.modules
        if (!modules.length) return false
        const englishCount = modules.filter((m) =>
            ['englisch', 'english'].includes((m.lehrsprache ?? '').toLowerCase())
        ).length
        return englishCount > modules.length / 2
    })

    const studiengang = computed(() =>
        studiengangStore.getStudiengangById(props.modulHandbuch.studiengangId)
    )
    const fachbereich = computed(() => fachbereichStore.getFachbereichByIri(studiengang.value.fachbereich))

    const fachbereichLabel = computed(() => {
        return `FB${fachbereich.value.hsaId} - ${fachbereich.value.kurzName}`
    })

    const fachbereichLongLabel = computed(() => {
        const prefix = isMhbEnglish.value ? 'Department' : 'Fachbereich'
        return `${prefix} ${fachbereich.value.name}`
    })

    const standortLabel = computed(() => {
        return `Campus ${studiengang.value.standort}`
    })

    const studienformLabel = computed(() => {
        return studiengang.value.studienformen.studienformen
            .map((sf) => (isMhbEnglish.value ? translate(sf.name) : sf.name))
            .join(', ')
    })

    const studiengangLabel = computed(() => {
        const abschlussLang = studiengang.value.abschlussArt.abschlussLang
        return `${isMhbEnglish.value ? translate(abschlussLang) : abschlussLang} ${studiengang.value.name}`
    })

    const dateLabel = computed(() => {
        const options = { year: 'numeric', month: 'numeric', day: 'numeric' }
        const dateTimeFormat = new Intl.DateTimeFormat('de-DE', options)
        const date = new Date(props.modulHandbuch.updatedAt)
        const dateString = dateTimeFormat.format(date)
        const semester = props.modulHandbuch.semester
        const prefix = isMhbEnglish.value ? 'Version:' : 'Stand:'
        return `${prefix} ${semester ?? dateString}`
    })
</script>

<template>
    <div data-test="titelblatt" class="bg-image-deckblatt">
        <div class="fachbereich d-flex flex-column">
            <div>
                {{ fachbereichLabel }}
            </div>
            <div class="fachbereich-lang">
                {{ fachbereichLongLabel }}
            </div>
            <div class="standort-öabeö">
                {{ standortLabel }}
            </div>
        </div>
        <div class="deckblatt-title">{{ isMhbEnglish ? 'Module description handbook' : 'Modulhandbuch' }}</div>
        <div class="studienformen-label">
            {{ studienformLabel }}
        </div>
        <div class="studiengang-label">
            {{ studiengangLabel }}
        </div>
        <div class="date-label">
            {{ dateLabel }}
        </div>
    </div>
</template>

<script></script>

<style scoped>
    .fachbereich {
        color: #13017c;
        font-size: 1.1em;
        font-family:
            Source Sans Pro,
            sans-serif;
        position: relative;
        left: 22%;
        top: 22%;
        max-width: 50%;
    }

    .fachbereich-lang {
        font-weight: 900;
    }

    .deckblatt-title {
        color: #e30613;
        font-size: 3em;
        font-weight: 700;
        font-family:
            Source Sans Pro,
            sans-serif;
        position: relative;
        left: 22%;
        top: 30%;
        max-width: 70%;
    }

    .studienformen-label {
        color: #13017c;
        font-size: 1.1em;
        font-family:
            Source Sans Pro,
            sans-serif;
        position: relative;
        left: 22%;
        top: 40%;
        max-width: 50%;
    }

    .studiengang-label {
        color: #13017c;
        font-size: 2em;
        font-weight: 600;
        font-family:
            Source Sans Pro,
            sans-serif;
        position: relative;
        left: 22%;
        top: 40%;
        max-width: 60%;
    }

    .date-label {
        color: #13017c;
        font-size: 1.2em;
        font-weight: 600;
        font-family:
            Source Sans Pro,
            sans-serif;
        position: relative;
        left: 70%;
        top: 66%;
        max-width: 30%;
    }

    .bg-image-deckblatt {
        background-image: url('../../../images/deckblatt.png');
        background-size: cover;
        background-repeat: no-repeat;
        width: 826px;
        height: 1169px;
        box-shadow: 1px 3px 6px 5px #e4e3ea;
    }
</style>
